<script setup>
  import { useMenuPermissions } from '@/composables/useMenuPermissions';
  import menuItems from '@/config/menus/nav-drawer-menu-items';
  import { useLayoutStore } from '@/stores/layout';

  import { router, usePage } from '@inertiajs/vue3';
  import { storeToRefs } from 'pinia';
  import { provide } from 'vue';

  const layout = useLayoutStore();
  const { navDrawer } = storeToRefs(layout);

  // Provide the click handler to all child components
  provide('onMenuItemClick', (item) => {
    if (item.to) {
      router.visit(item.to);
    }
    // Close the drawer after clicking a menu item (optional)
    layout.closeNavDrawer();
  });

  // Pull roles & permissions from Inertia shared props
  // Reactivity make sure changes from Laravel backend are reflected immediately at every request
  const page = usePage();
  const userRoles = computed(() => new Set(page.props.auth?.roles ?? []));
  const userPermissions = computed(() => new Set(page.props.auth?.permissions ?? []));

  const { getFilteredMenuItems } = useMenuPermissions(userRoles, userPermissions);
  const filteredMenuItems = computed(() => getFilteredMenuItems(menuItems));
</script>

<template>
  <v-navigation-drawer v-model="navDrawer" width="300" temporary app>
    <div class="pa-5">
      <v-img src="/images/logo.svg" class="img-contain" max-width="250" />
    </div>

    <v-list class="pa-2" density="compact" nav>
      <template v-for="(item, index) in filteredMenuItems">
        <v-divider v-if="item.divider" :key="`divider-${index}`" class="my-2" />

        <NavDrawerHeader
          v-else-if="item.header"
          :item="item"
          :key="`header-${item.title || index}`"
        />

        <NavDrawerGroup
          v-else-if="item.children"
          :item="item"
          :key="`group-${item.title || index}`"
        />

        <NavDrawerItem v-else :item="item" :key="`item-${item.title || index}`" />
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<style lang="scss" scoped>
  @use '~/styles/navigation' as *;
</style>
